import React, { useState, useEffect, useContext } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { EventDatetimeContext } from 'components/EventDatetime';
import DinCondensedFontOTF from 'assets/fonts/D-DIN.otf';
import DinCondensedFontEOT from 'assets/fonts/D-DIN.eot';
import remoteTimeNow from 'libs/remote-time-now';

const CountdownContainer = styled.div`
  @font-face {
    font-family: din;
    src:
      url(${DinCondensedFontOTF}),
      url(${DinCondensedFontEOT}?#iefix) format('embedded-opentype');
  }

  display: flex;
  flex-shrink: 0;
  justify-content: space-between;
  max-width: 600px;
  width: 100%;
`;

const FieldContainer = styled.div`
  display: flex;
  flex-direction: column;
  color: #FFF;
  font-size: 24px;
  text-align: center;
  align-items: center;
`;

const NumberContainer = styled.div`
  width: min(20vh, 20vw);
  height: min(20vh, 20vw);
  background: #484848;
  border-radius: 4px;
  color: #FFF;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  font-family: din;
  font-weight: 500;
  line-height: 60px;
  text-align: center;
  font-size: 50px;

  @media screen and (min-width: 300px) {
    font-size: 60px;
  }

  @media screen and (min-width: 400px) {
    font-size: 64px;
  }

  @media screen and (min-width: 600px) {
    max-height: 124px;
    max-width: 124px;
    font-size: 80px;
  }

  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    width: 20vw;
    height: 20vw;
  }
`;

const NumberDeleteLine = styled.div`
  border: 3px solid #484848;
  position: absolute;
  width: 100%;

  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    top: 50%;
    left: 0;
  }
`;

const UnitContainer = styled.div`
  color: #FFF;
  display: block;
  margin-top: 2vh;
  font-family: din;
  font-weight: 500;
  line-height: 24px;
  text-align: center;
  font-size: 14px;

  @media screen and (min-width: 300px) {
    font-size: 16px;
  }

  @media screen and (min-width: 400px) {
    font-size: 20px;
  }

  @media screen and (min-width: 500px) {
    font-size: 22px;
  }

  @media screen and (min-width: 600px) {
    font-size: 24px;
  }
`;

const leftPadZero = (s) => `${s}`.padStart(2, '0');
const justifyUnit = (unit, value) => (value > 1 ? `${unit}S` : unit);

const timeDiff = (destination, source) => ({
  days: destination.diff(source, 'days'),
  hours: destination.diff(source, 'hours') % 24,
  minutes: destination.diff(source, 'minutes') % 60,
  seconds: destination.diff(source, 'seconds') % 60,
});

const useCountdown = () => {
  const { startDatetime } = useContext(EventDatetimeContext);
  const [days, setDays] = useState(0);
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      const newNow = remoteTimeNow();
      const td = timeDiff(startDatetime, newNow);
      setDays(td.days);
      setHours(td.hours);
      setMinutes(td.minutes);
      setSeconds(td.seconds);
    }, 1000);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDatetime]);

  return {
    days, hours, minutes, seconds,
  };
};

const Countdown = ({ className }) => {
  const {
    days, hours, minutes, seconds,
  } = useCountdown();

  return (
    <CountdownContainer className={className}>
      <FieldContainer>
        <NumberContainer>
          <NumberDeleteLine />
          {leftPadZero(days)}
        </NumberContainer>
        <UnitContainer>{justifyUnit('DAY', days)}</UnitContainer>
      </FieldContainer>

      <FieldContainer>
        <NumberContainer>
          <NumberDeleteLine />
          {leftPadZero(hours)}
        </NumberContainer>
        <UnitContainer>{justifyUnit('HOUR', hours)}</UnitContainer>
      </FieldContainer>

      <FieldContainer>
        <NumberContainer>
          <NumberDeleteLine />
          {leftPadZero(minutes)}
        </NumberContainer>
        <UnitContainer>{justifyUnit('MINUTE', minutes)}</UnitContainer>
      </FieldContainer>

      <FieldContainer>
        <NumberContainer>
          <NumberDeleteLine />
          {leftPadZero(seconds)}
        </NumberContainer>
        <UnitContainer>{justifyUnit('SECOND', seconds)}</UnitContainer>
      </FieldContainer>
    </CountdownContainer>
  );
};

Countdown.propTypes = {
  className: PropTypes.string,
};

Countdown.defaultProps = {
  className: undefined,
};

export default Countdown;
